function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useStore } from "../../../store/store-context";
import { useTemplateVariableDetection } from "./hooks/useTemplateVariableDetection";
import { useVariableResolution } from "./hooks/useVariableResolution";
import { normalizeVariableLabel } from "./TemplateVariables.utils";
import { jsx as _jsx } from "react/jsx-runtime";
const EMPTY_TEMPLATE_VARS = [];
const TemplateVariablesContext = /*#__PURE__*/createContext(null);
export function TemplateVariablesProvider(_ref) {
  let {
    contact,
    worksAt,
    agreementName,
    effectiveDate,
    initialTemplateVariableValues,
    textFields,
    richTextFields,
    children
  } = _ref;
  const businessDefaults = useStore(state => {
    var _state$userInfo$busin, _state$userInfo;
    return (_state$userInfo$busin = (_state$userInfo = state.userInfo) === null || _state$userInfo === void 0 || (_state$userInfo = _state$userInfo.businessSettings()) === null || _state$userInfo === void 0 ? void 0 : _state$userInfo.templateVariables) !== null && _state$userInfo$busin !== void 0 ? _state$userInfo$busin : EMPTY_TEMPLATE_VARS;
  });
  const {
    detectedVariables,
    customVariables,
    systemVariables
  } = useTemplateVariableDetection(textFields, richTextFields);
  const [templateVariableValues, setTemplateVariableValues] = useState(() => {
    if (!initialTemplateVariableValues) {
      return {};
    }
    const normalized = {};
    for (const [key, value] of Object.entries(initialTemplateVariableValues)) {
      normalized[normalizeVariableLabel(key)] = value;
    }
    return normalized;
  });
  const setTemplateVariableValue = useCallback((label, value) => {
    setTemplateVariableValues(prev => _objectSpread(_objectSpread({}, prev), {}, {
      [normalizeVariableLabel(label)]: value
    }));
  }, []);
  const [currentDate] = useState(() => new Date());
  const resolutionContext = useMemo(() => ({
    clientCompanyName: contact === null || contact === void 0 ? void 0 : contact.companyName(),
    clientFirstName: contact === null || contact === void 0 ? void 0 : contact.firstName(),
    clientLastName: contact === null || contact === void 0 ? void 0 : contact.lastName(),
    clientEmail: contact === null || contact === void 0 ? void 0 : contact.email(),
    vendorBusinessName: worksAt.name,
    agreementName: agreementName !== null && agreementName !== void 0 ? agreementName : undefined,
    effectiveDate,
    currentDate
  }), [contact, worksAt.name, agreementName, effectiveDate, currentDate]);
  const resolvedValues = useVariableResolution(detectedVariables, resolutionContext, templateVariableValues, businessDefaults);
  const unresolvedCustomVariables = useMemo(() => customVariables.filter(label => resolvedValues[normalizeVariableLabel(label)] === undefined), [customVariables, resolvedValues]);
  const value = useMemo(() => ({
    detectedVariables,
    customVariables,
    systemVariables,
    resolvedValues,
    templateVariableValues,
    setTemplateVariableValue,
    unresolvedCustomVariables,
    hasVariables: detectedVariables.length > 0
  }), [detectedVariables, customVariables, systemVariables, resolvedValues, templateVariableValues, setTemplateVariableValue, unresolvedCustomVariables]);
  return /*#__PURE__*/_jsx(TemplateVariablesContext.Provider, {
    value: value,
    children: children
  });
}
const defaultContextValue = {
  detectedVariables: [],
  customVariables: [],
  systemVariables: [],
  resolvedValues: {},
  templateVariableValues: {},
  setTemplateVariableValue: () => {},
  unresolvedCustomVariables: [],
  hasVariables: false
};
export function useTemplateVariables() {
  var _useContext;
  return (_useContext = useContext(TemplateVariablesContext)) !== null && _useContext !== void 0 ? _useContext : defaultContextValue;
}
export function TemplateVariablesPortalProvider(_ref2) {
  let {
    value,
    children
  } = _ref2;
  return /*#__PURE__*/_jsx(TemplateVariablesContext.Provider, {
    value: value,
    children: children
  });
}